import { PageHeader } from "@/components/common/PageHeader";
import { Box } from "@mui/material";
import { Tab, useRecordContext, TopToolbar, DeleteButton } from "react-admin";
import { useMemo } from "react";
import { AuditLogBox } from "@/components/common/AuditLogBox";
import { ResourceName, Vendor } from "@/types";
import { StyledShow } from "@/components/ui/react-admin/StyledShow";
import { getResourceIcon } from "@/utils/icons";
import { StyledTabbedShowLayout } from "@/components/ui/react-admin/StyledTabbedShowLayout";
import { PageContent } from "@/components/common/PageLayout";
import { VendorOverviewTab } from "./components/VendorOverviewTab";
import { CarrierReportingTab } from "./components/CarrierReportingTab";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";

const VendorIcon = getResourceIcon(ResourceName.VENDORS);

export const VendorsShow = () => {
  return (
    <>
      <StyledShow actions={false}>
        <VendorsShowContent />
      </StyledShow>
    </>
  );
};

const VendorsShowContent = () => {
  const vendorRecord = useRecordContext<Vendor>();

  const subtitle = useMemo(() => {
    if (!vendorRecord) return "";
    const types = [];
    if (vendorRecord.is_sl_carrier) types.push("Stop-Loss Carrier");
    if (vendorRecord.is_tpa)
      types.push(vendorRecord.tpa_is_buca ? "Third-Party Administrator (BUCA)" : "Third-Party Administrator");
    if (vendorRecord.is_rx_vendor) types.push("Pharmacy Benefit Manager");
    return types.join(" • ");
  }, [vendorRecord]);

  return (
    <Box>
      <PageHeader
        titleKey="company_name"
        customSubtitle={subtitle}
        rightContent={<VendorsShowActions />}
        headerIcon={<VendorIcon sx={{ fontSize: 28, color: "primary.main" }} />}
      />
      <PageContent>
        <VendorShowTabbedLayout />
      </PageContent>
    </Box>
  );
};

const VendorShowTabbedLayout = () => {
  return (
    <StyledTabbedShowLayout>
      <Tab label="Reporting">
        <CarrierReportingTab />
      </Tab>
      <Tab label="Overview">
        <VendorOverviewTab />
      </Tab>
      <Tab label="Audit Log">
        <AuditLogBox tableName={ResourceName.VENDORS} />
      </Tab>
    </StyledTabbedShowLayout>
  );
};

const VendorsShowActions = () => {
  const vendorRecord = useRecordContext<Vendor>();
  const { canDelete } = useResourcePermissions(ResourceName.VENDORS);

  // Don't render anything if user has no edit or delete permissions
  if (!canDelete) {
    return null;
  }

  return (
    <TopToolbar sx={{ marginTop: "-30px" }}>
      {canDelete && (
        <DeleteButton
          mutationMode="pessimistic"
          redirect={"/vendors"}
          successMessage="Vendor deleted successfully"
          confirmTitle={`Delete ${vendorRecord?.company_name}`}
          confirmContent="You will not be able to recover this record. Are you sure?"
        />
      )}
    </TopToolbar>
  );
};
